import React, { useRef, useState, useEffect } from "react";
import qboApi from '../../../api/qboApi';
import { Send, Loader2, FileText, Sheet, CheckCircle2, X } from 'lucide-react';
import qbLogo from '../../../assets/qb-logo.png';

interface QuestionInputProps {
    onSendMessage: (message: string) => void;
    onFileUpload?: (file: File) => void;
    hasStarted: boolean;
}

const QuestionInput: React.FC<QuestionInputProps> = ({ onSendMessage, onFileUpload, hasStarted }) => {
    const [input, setInput] = useState("");
    const [uploadingFile, setUploadingFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); // State for the purple line

    const fileInputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const [isVerified, setIsVerified] = useState(() => {
        return sessionStorage.getItem('approval_status') === 'true';
    });

    // --- Helpers for File Icon & Size ---
    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
    };

    const getFileIconConfig = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        if (['csv', 'xls', 'xlsx'].includes(extension || '')) {
            return { Icon: Sheet, color: 'text-green-600', bg: 'bg-green-50' };
        }
        return { Icon: FileText, color: 'text-red-600', bg: 'bg-red-50' };
    };

    const handleSubmit = (e: React.FormEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (input.trim()) {
            onSendMessage(input);
            setInput("");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    // Auto-resize textarea
    useEffect(() => {
        if (textAreaRef.current) {
            const maxHeight = 200;
            textAreaRef.current.style.height = "auto";
            const newHeight = Math.min(textAreaRef.current.scrollHeight, maxHeight);
            textAreaRef.current.style.height = `${newHeight}px`;
            textAreaRef.current.style.overflowY =
                textAreaRef.current.scrollHeight > maxHeight ? "auto" : "hidden";
        }
    }, [input]);

    const handleFileAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingFile(file);
        setIsUploading(true);
        setUploadProgress(0);

        // 1. Start Simulated Progress (runs up to 90% while waiting for API)
        const interval = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 90) return 90;
                return prev + 5; // Speed of the purple line
            });
        }, 100);

        try {
            if (onFileUpload) {
                await onFileUpload(file);
            }
            // 2. Upload Complete: Push to 100%
            setUploadProgress(100);
            clearInterval(interval);

            // 3. Keep the "Completed" card visible for a moment before clearing
            setTimeout(() => {
                setIsUploading(false);
                setUploadingFile(null);
            }, 1500); // Shows "Completed" state for 1.5s

        } catch (err) {
            console.error("Upload failed:", err);
            clearInterval(interval);
            setIsUploading(false);
            setUploadingFile(null);
        }
    };

    const handleQboConnect = async () => {
        try {
            const { auth_url } = await qboApi.initiateQboConnection();
            if (auth_url) window.open(auth_url, "_blank", "noopener,noreferrer");
        } catch (error) {
            alert("Unable to connect to QuickBooks. Please try again later.");
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-full">
            <div className="space-y-2">
                {/* Main Container: 
                    - Changed h-[132px] to min-h-[132px] h-auto to accommodate the file card 
                    - Added flex-col to stack File Card + Text Area
                */}
                <div className={`relative w-full min-h-[132px] h-auto flex flex-col justify-between bg-white border border-gray-200 rounded-xl p-5 shadow-sm transition-all duration-300`}>

                    {!isVerified && (
                        <div className="disabled-view absolute inset-0 bg-white/50 z-10 cursor-not-allowed rounded-xl" />
                    )}

                    {/* --- TOP SECTION: INPUT AREA (Flex Column) --- */}
                    <div className="flex-1 w-full flex flex-col gap-3">

                        {/* FILE UPLOAD CARD (The Long Block) */}
                        {uploadingFile && (
                            <div className="w-full border border-gray-200 rounded-lg p-3 flex items-center gap-3 bg-white shadow-sm animate-in fade-in slide-in-from-bottom-2">
                                {/* File Icon */}
                                {(() => {
                                    const { Icon, color, bg } = getFileIconConfig(uploadingFile.name);
                                    return (
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                                            <Icon className={`w-6 h-6 ${color}`} />
                                        </div>
                                    );
                                })()}

                                {/* File Info & Progress */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-center mb-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {uploadingFile.name}
                                        </p>
                                    </div>

                                    {/* Status Text & Progress Bar */}
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                            {uploadProgress < 100 ? (
                                                <>
                                                    <span>
                                                        {formatSize((uploadingFile.size * uploadProgress) / 100)} of {formatSize(uploadingFile.size)}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-gray-700">
                                                        • <Loader2 className="w-3 h-3 animate-spin" /> Uploading...
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>{formatSize(uploadingFile.size)}</span>
                                                    <span className="flex items-center gap-1 text-green-600 font-medium">
                                                        • <CheckCircle2 className="w-3 h-3" /> Completed
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        {/* Progress Bar Container */}
                                        <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                                            {/* Running Purple Line */}
                                            <div
                                                className="h-full bg-purple-600 transition-all duration-200 ease-out rounded-full"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TEXT INPUT */}
                        <textarea
                            ref={textAreaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type a message..."
                            className="w-full min-h-[40px] text-zinc-600 bg-transparent border-none outline-none resize-none overflow-y-auto"
                            style={{ display: uploadingFile && isUploading ? 'none' : 'block' }}
                        // Optional: Hide textarea only during active upload if you want to save space, 
                        // or remove style prop to keep both visible.
                        />
                    </div>

                    {/* --- BOTTOM SECTION: BUTTONS --- */}
                    <div className="w-full h-[37px] flex items-center justify-between mt-4">
                        <div className="flex items-center gap-4">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileAttach}
                                className="hidden"
                                accept=".pdf,.xlsx,.xls,.csv"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                                className={`w-[105px] h-[37px] flex items-center justify-center gap-2 bg-black border border-gray-200 rounded-lg hover:bg-black/80 transition-colors px-3 py-1 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <img src="/images/icons/Attach.png" alt="Attach" className="w-4 h-4 object-contain" />
                                <span className="font-sans text-sm font-medium text-white">Attach</span>
                            </button>
                            <button
                                type="button"
                                onClick={handleQboConnect}
                                className="w-[36px] h-[36px] rounded-full overflow-hidden bg-white shadow-sm flex items-center justify-center hover:opacity-90 transition-opacity"
                            >
                                <img src="/images/icons/quickbooks-2.svg" alt="QuickBooks" className="w-full h-full object-contain" />
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={!input.trim() || isUploading}
                            className={`${input.trim() && !isUploading ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-300 cursor-not-allowed'} w-9 h-9 flex items-center justify-center text-white rounded-full transition-colors`}
                        >
                            <Send className="h-4 w-4 ml-0.5" />
                        </button>
                    </div>
                </div>

                <div className="text-center font-inter text-[10px] font-normal leading-[100%] tracking-[0px] text-[#989DA3]">
                    AI CFO can make mistakes. Please double-check responses.
                </div>
            </div>
        </form>
    );
};

export default QuestionInput;
